import { PhosphorPencilAnchor } from "components/Atoms/PhosphorIconAnchor";
import { PhosphorLinkBreakButton } from "components/Atoms/PhosphorIconButton";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import React from "react";
import { routes } from "utilities/routing/generatedRoutes";
import { DeleteDialog, Modal } from "../../components";
import { getDocumentApi } from "../../utilities/apiUtils";
import LinkShowsModal from "./LinkShowsModal";
import { ShowListWithSales } from "./ShowListWithSales";
import { useQueryClient } from "@tanstack/react-query";

interface Props {
  documentType: server.dto.DocumentTypes
  displayLinkShowsModal: boolean
  onDisplayLinkShowsModalToggled: () => void

  onShowsChange?: (shows: server.dto.ShowForListInternal[]) => void
  config: server.dto.LinkedShowPanelConfig
  defaultBookkeeping: server.dto.BookkeepingSmall
  queryKey: any[]
  initialShowCount: number

  disabled?: boolean
}

const LinkedShows = (props: Props) => {
  const [isUnlinkingId, setIsUnlinkingId] = React.useState<number>(undefined);
  const [showCount, setShowCount] = React.useState(props.initialShowCount);

  const onUnlinkClicked = (showId: number) => {
    if (isUnlinkingId) {
      return;
    }

    setIsUnlinkingId(showId);
  };

  const queryClient = useQueryClient();

  const unlink = async () => {
    const documentApi = getDocumentApi(props.documentType);

    await documentApi.UnlinkShow(props.config.itemId, isUnlinkingId);

    queryClient.invalidateQueries({ queryKey: props.queryKey });
    setIsUnlinkingId(undefined);
  };

  const onLinkedShowsWereSaved = () => {
    queryClient.invalidateQueries({ queryKey: props.queryKey });
    props.onDisplayLinkShowsModalToggled();

    window.refreshPayment?.();
    window.refreshProducts?.();
  };

  const { refToScrollTo } = useShortcutPanel({
    type: "show",
    label: site.currentEnvironment.show.plural,
    totalItems: showCount ?? 0,
    onAddClicked: (props.config.canLinkUnlink && !props.disabled) ? props.onDisplayLinkShowsModalToggled : undefined
  });

  return (<>
    <div ref={refToScrollTo}></div>

    <ShowListWithSales
      perPage={10}
      queryKey={props.queryKey}
      onFetch={(result) => {
        setShowCount(result.pagination.total);
        props.onShowsChange?.(result.data);
      }}
      instruction={{
        documentIds: [props.config.itemId],
      }}
      button1={(show) => <div className="s1_button_cell">
        {(!props.disabled && props.config.canLinkUnlink) && <PhosphorLinkBreakButton
          layout="in-square"
          onClick={() => onUnlinkClicked(show.showId)} />}
      </div>}
      button2={(show) => <div className="s1_button_cell">
        {(!props.disabled && show.permissions.canModify) && <PhosphorPencilAnchor
          href={routes.show_Edit(show.showId).absolute}
          layout="in-square" />}
      </div>}
    />

    <DeleteDialog
      isOpen={isUnlinkingId != null}
      title={RESX.GeneralWarnings.Warning}
      message={RESX.Document.UnlinkWarning}
      onDelete={unlink}
      buttonProps={{
        primary: true,
        color: undefined,
        content: RESX.Buttons.BtnOk
      }}
      handleClose={() => { setIsUnlinkingId(undefined); }}
    />

    <Modal
      isOpen={props.displayLinkShowsModal}
      handleClose={props.onDisplayLinkShowsModalToggled}
      width="large"
      displayHeader={false}
      height={850}
    >
      <LinkShowsModal
        documentId={props.config.itemId}
        defaultFilters={{
          ...props.config.defaultFilters,
          bookkeepings: props.defaultBookkeeping ? [props.defaultBookkeeping] : []
        }}
        initialInstruction={{
          ...props.config.initialInstruction,
          bookkeepingIds: props.defaultBookkeeping ? [props.defaultBookkeeping.bookkeepingId] : []
        }}
        documentType={props.documentType}
        onLinkedShowsSaved={onLinkedShowsWereSaved}
      />
    </Modal>
  </>);
};

export default LinkedShows;